import routeConstructor from "./routeConstructor";
import TrackingService from "../services/tracking/TrackingService";
export const GET_TICKETS = "getTickets";
export const MERCHANDISE = "merchandise";
export const DETAILS = "details";
export const ASSIGNED_TICKETS = "assignedTickets";
export const SEATING_MAP = "seatingMap";
export const PAYMENT = "payment";
export const AFTER_CHECKOUT = "afterCheckout";
export const COMPLETE = "complete";

const routes = [
  GET_TICKETS,
  MERCHANDISE,
  DETAILS,
  ASSIGNED_TICKETS,
  SEATING_MAP,
  PAYMENT,
  AFTER_CHECKOUT,
  COMPLETE
];

const giftCardRoutes = [GET_TICKETS, DETAILS, PAYMENT, COMPLETE];

export const getGiftCardRoutes = (giftCardData, orderData) => {
  const routeLinks = routeConstructor(
    giftCardData.slug,
    orderData ? orderData._id : false
  ).giftCard;
  const activeRoutes = determineActiveRoutes(orderData, false);
  const shouldShowOnBreadcrumb = shouldShowBreadCrumbStep();
  const buttonLabels = getButtonLabels(false, false);
  const routeLabels = getRouteLabels(false, false);
  return giftCardRoutes.map(route => {
    return {
      routeId: route,
      ...routeLabels[route],
      path: routeLinks[route],
      active: activeRoutes[route],
      required: true,
      showOnBreadcrumb: shouldShowOnBreadcrumb[route],
      buttonLabel: buttonLabels[route]
    };
  });
};

export const getApplicableRoutes = (eventData, orderData) => {
  const routeLabels = getRouteLabels(eventData, orderData);
  const requiredRoutes = determineRequiredRoutes(eventData, orderData);
  const routeLinks = routeConstructor(
    eventData ? eventData.slug : false,
    orderData ? orderData._id : false
  ).general;
  const activeRoutes = determineActiveRoutes(
    orderData,
    requiredRoutes[SEATING_MAP]
  );
  const routeTransitionCalls = getRouteTransitionCalls(
    requiredRoutes[AFTER_CHECKOUT]
  );
  const shouldShowOnBreadcrumb = shouldShowBreadCrumbStep();
  const buttonLabels = getButtonLabels(
    requiredRoutes[ASSIGNED_TICKETS],
    requiredRoutes[SEATING_MAP]
  );
  return routes
    .map(route => {
      return {
        routeId: route,
        ...routeLabels[route],
        path: routeLinks[route],
        active: activeRoutes[route],
        required: requiredRoutes[route],
        transitionCall: routeTransitionCalls[route],
        showOnBreadcrumb: shouldShowOnBreadcrumb[route],
        buttonLabel: buttonLabels[route]
      };
    })
    .filter(route => route.required);
};

const getButtonLabels = (hasAssignedTickets, hasSeatingMap) => {
  const paymentLabel = "Checkout";
  const seatingLabel = "Select Seats";
  let detailsText = hasSeatingMap ? seatingLabel : "Save & Checkout";
  detailsText = hasAssignedTickets ? "Assign tickets" : detailsText;
  const assignedTickets = hasSeatingMap ? seatingLabel : paymentLabel;

  return {
    getTickets: "Enter Details",
    merchandise: "Enter Details",
    details: detailsText,
    seatingMap: paymentLabel,
    assignedTickets,
    payment: "Pay now",
    afterCheckout: "Submit",
    complete: false
  };
};

const getRouteLabels = (eventData, orderData) => {
  const usingAssignedTickets = eventUsingAssignedTickets(eventData, orderData);
  return {
    getTickets: {
      desktopLabel: "Select Tickets",
      mobileLabel: "Tickets"
    },
    merchandise: {
      desktopLabel: "Add Ons",
      mobileLabel: "Add Ons"
    },
    details: {
      desktopLabel: usingAssignedTickets ? "Buyer Details" : "Details",
      mobileLabel: "Details"
    },
    seatingMap: {
      desktopLabel: "Seats",
      mobileLabel: "Seats"
    },
    assignedTickets: {
      desktopLabel: "Assign Tickets",
      mobileLabel: "Assign"
    },
    payment: {
      desktopLabel: "Payment",
      mobileLabel: "Payment"
    },
    afterCheckout: {
      desktopLabel: "After Checkout",
      mobileLabel: "After Checkout"
    },
    complete: {
      desktopLabel: "Complete",
      mobileLabel: "Complete"
    }
  };
};
/* istanbul ignore next */
const shouldShowBreadCrumbStep = () => {
  return {
    getTickets: true,
    merchandise: true,
    details: true,
    seatingMap: true,
    assignedTickets: true,
    payment: true,
    afterCheckout: false,
    complete: false
  };
};

const determineRequiredRoutes = (eventData, orderData) => {
  return {
    getTickets: true,
    merchandise: merchOffered(eventData, orderData),
    details: true,
    seatingMap: seatingSelectionRequired(eventData, orderData),
    assignedTickets: eventUsingAssignedTickets(eventData, orderData),
    payment: paymentRequired(orderData),
    afterCheckout: eventHasAfterCheckoutQuestions(eventData, orderData),
    complete: true
  };
};

/**
 * This gets ignored for now. The action for constructing routes is overriding the logic.
 */
const determineActiveRoutes = (orderData, seatingRequired) => {
  return {
    getTickets: true,
    merchandise: merchandiseActive(orderData),
    details: detailsActive(orderData),
    seatingMap: seatingMapActive(orderData),
    assignedTickets: assignedTicketsMapActive(orderData, seatingRequired),
    payment: paymentActive(orderData, seatingRequired),
    afterCheckout: true,
    complete: true
  };
};
/* istanbul ignore next */
const getRouteTransitionCalls = afterCheckoutRequired => {
  return {
    getTickets: state => {
      TrackingService.ticketsAdded(state.event.data, state.order.data);
    },
    merchandise: state => {},
    details: state => {
      TrackingService.enteredDetails(state.event.data, state.order.data);
      if (state.order.data.totals.total === 0) {
        TrackingService.complete(state.event.data, state.order.data);
      }
    },
    seatingMap: state => {},
    assignedTickets: state => {},
    payment: state => {
      TrackingService.complete(state.event.data, state.order.data);
    },
    afterCheckout: state => {},
    complete: state => {}
  };
};

const merchandiseActive = orderData => {
  if (orderData) {
    return true;
  }
  return false;
};

const detailsActive = orderData => {
  if (orderData) {
    return true;
  }
  return false;
};

const seatingMapActive = orderData => {
  if (orderData && orderData.firstName) {
    return true;
  }
  return false;
};

const assignedTicketsMapActive = orderData => {
  if (orderData && orderData.firstName) {
    return true;
  }
  return false;
};

const paymentActive = (orderData, seatingRequired) => {
  if (orderData) {
    const orderDetailsFilled = !!(orderData && orderData.firstName);
    if (!orderDetailsFilled || !seatingRequired) {
      return orderDetailsFilled;
    }
    const tickets = orderData.tickets || [];
    return tickets.reduce((foundAssignment, ticket) => {
      if (foundAssignment) {
        return foundAssignment;
      }
      return (
        true &&
        ticket.seatingLocation &&
        ticket.seatingLocation.section &&
        ticket.seatingLocation.section.id !== "unassigned"
      );
    }, false);
  }
  return false;
};

const seatingSelectionRequired = (eventData, orderData) => {
  let seatingMapId;
  if (orderData) {
    const { tickets } = orderData;
    const event = eventData;
    if (event && event.seatingMaps && tickets) {
      event.seatingMaps.forEach(seatingMap => {
        const isPublic = seatingMap.public !== false;
        seatingMap.ticketTypeSet.forEach(ticketType => {
          const containsPackageTicket =
            tickets.filter(
              ticket => String(ticket.packageId) === String(ticketType)
            ).length > 0;
          const containsTicketType =
            tickets.filter(
              ticket => String(ticket.ticketTypeId) === String(ticketType)
            ).length > 0;
          if (
            (containsPackageTicket || containsTicketType) &&
            !seatingMapId &&
            isPublic
          ) {
            seatingMapId = seatingMap.id;
          }
        });
      });
    }
  }
  return !!seatingMapId;
};

const merchOffered = (eventData, orderData) => {
  if (!orderData || !eventData) {
    return false;
  }
  if (eventData.isFreeOnly) {
    return false;
  }
  const includesMerchOffer = orderData.orderIncludesMerchOffer;

  return !!includesMerchOffer;
};

const eventUsingAssignedTickets = (eventData, orderData) => {
  return !!(
    eventData &&
    eventData.settings &&
    eventData.settings.enableTicketAssigning &&
    orderData &&
    orderData.tickets &&
    orderData.tickets.length > 0
  );
};

const paymentRequired = orderData => {
  if (!orderData) {
    return true;
  }
  //payment ain't required if the order is manual
  if (orderData.manualOrder) {
    return false;
  }
  const orderHasTotals = orderData.totals && orderData.totals.total > 0;
  const ticketsAreNotFree =
    orderData.tickets &&
    orderData.tickets.reduce((costsMoney, ticket) => {
      return costsMoney || ticket.total > 0;
    }, false);
  return !!(orderHasTotals || ticketsAreNotFree);
};

const eventHasAfterCheckoutQuestions = (eventData, orderData) => {
  if (!eventData || !orderData || !orderData.tickets) {
    return false;
  }
  const additionalQuestions = eventData.additionalQuestions
    ? eventData.additionalQuestions
    : [];
  const afterCheckoutQuestions = additionalQuestions.filter(
    q => q.askAfterCheckout
  );
  return orderData.tickets.reduce((found, ticket) => {
    afterCheckoutQuestions.forEach(q => {
      if (
        !q.appliesTo ||
        !q.appliesTo.length ||
        q.appliesTo.indexOf(ticket.ticketTypeId) !== -1
      ) {
        found = true;
      }
    });
    return found;
  }, false);
};

export const privateMethods = {
  getButtonLabels,
  getRouteLabels,
  determineRequiredRoutes,
  determineActiveRoutes,
  getRouteTransitionCalls,
  merchandiseActive,
  detailsActive,
  seatingMapActive,
  assignedTicketsMapActive,
  paymentActive,
  seatingSelectionRequired,
  merchOffered,
  eventUsingAssignedTickets,
  paymentRequired,
  eventHasAfterCheckoutQuestions
};
